/**
 * Formats decimal numbers by removing trailing zeros and using Estonian comma separator
 * @param {string|number} value - The decimal value to format
 * @returns {string} - Formatted value without trailing zeros, using comma as decimal separator
 */
export const formatDecimal = (value) => {
  if (value === null || value === undefined) return value;
  if (value === "—" || value === "-") return value;
  if (value === "") return value; // keep empty as empty so UI fallbacks work

  // Support both comma and dot inputs, but output with comma
  const normalized = value.toString().replace(',', '.');
  const num = Number(normalized);
  if (!isFinite(num)) return value;

  // Use JS number formatting to drop unnecessary trailing zeros,
  // then convert decimal separator back to comma for Estonian locale.
  return num.toString().replace('.', ',');
};

/**
 * Normalizes decimal input by converting comma to dot for API/calculation purposes
 * @param {string} value - The input value that may contain comma
 * @returns {string} - Value with comma converted to dot
 */
export const normalizeDecimal = (value) => {
  if (!value) return value;
  return value.toString().replace(',', '.');
};

/**
 * Validates if a value is a valid decimal number (supports both comma and dot)
 * @param {string} value - The value to validate
 * @param {number} maxDecimals - Maximum decimal places allowed (default: 3)
 * @returns {boolean} - True if valid decimal
 */
export const isValidDecimal = (value, maxDecimals = 3) => {
  if (!value || value === "") return false;
  
  // Normalize comma to dot for validation
  const normalized = normalizeDecimal(value);
  const num = Number(normalized);
  
  if (isNaN(num) || num < 0) return false;

  // Check decimal places
  const parts = normalized.split(".");
  if (parts.length > 2) return false; // Invalid format like 1.2.3
  if (parts.length === 2 && parts[1].length > maxDecimals) return false;

  return true;
};

/**
 * Formats date to Estonian format
 * @param {string|Date} dateValue - Date to format
 * @returns {string} - Formatted date string
 */
export const formatDate = (dateValue) => {
  if (!dateValue) return "—";
  try {
    const date = new Date(dateValue);
    date.setHours(0, 0, 0, 0);
    return date.toLocaleDateString("et-EE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch (error) {
    return "—";
  }
}; 
